/**
 * Safe in-body JSON-LD emitter.
 *
 * Every structured-data graph on the site goes through this component instead
 * of a hand-rolled `<script type="application/ld+json" dangerouslySetInnerHTML>`.
 * Reasons:
 *
 * 1. Script breakout — a document title containing `</script>` would end the
 *    script element early and dump the rest of the graph into the DOM as text.
 *    We escape `<`, `>` and `&` to their `\uXXXX` JSON escapes, which stay
 *    byte-identical after `JSON.parse` for crawlers.
 * 2. Safari hydration hangs — literal U+2028 / U+2029 and C0 control bytes
 *    (notably NUL) inside inline scripts break WebKit's parser. We strip or
 *    escape them here, so a single bad character in generated content can never
 *    white-screen a page again.
 * 3. Empty graphs render nothing instead of emitting `null`, which Rich Results
 *    reports as an invalid item.
 */

import { compactJsonLd } from "@/lib/jsonld-compact";

const ESCAPES: Record<string, string> = {
  "<": "\\u003c",
  ">": "\\u003e",
  "&": "\\u0026",
  "\u2028": "\\u2028",
  "\u2029": "\\u2029",
};

/** Serialize a structured-data object into an inline-script-safe string. */
export function serializeJsonLd(data: unknown): string {
  let json: string;
  try {
    // Compaction is validator-neutral: same entities, fewer bytes.
    json = JSON.stringify(compactJsonLd(data));
  } catch {
    return "";
  }
  if (!json || json === "null" || json === "{}" || json === "[]" || json === "undefined") return "";

  return json
    // Drop C0 controls (incl. NUL) that JSON.stringify leaves raw in some runtimes.
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, "")
    .replace(/[<>&\u2028\u2029]/g, (ch) => ESCAPES[ch] ?? ch);
}

export function JsonLd({ data }: { data: unknown }) {
  const html = serializeJsonLd(data);
  if (!html) return null;
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: html }} />;
}
